import React, { useEffect, useState } from "react";
import {
    Drawer,
    Descriptions,
    Divider,
    Tag,
    Typography,
    Button,
    Space,
    Spin,
    Empty,
    message,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { paymentAPIWithErrorHandling } from "../../../api/payment/payment";

const { Text } = Typography;

const METHOD_LABEL = {
    CASH: { text: "Tiền mặt", color: "green" },
    BANK_TRANSFER: { text: "Chuyển khoản", color: "blue" },
    CARD: { text: "Thẻ", color: "orange" },
    MOMO: { text: "Momo", color: "purple" },
};

const FEE_STATUS_LABEL = {
    UNPAID: { text: "Chưa thu", color: "red" },
    PARTIAL: { text: "Thu một phần", color: "orange" },
    PAID: { text: "Đã thu", color: "green" },
    CANCELLED: { text: "Đã huỷ", color: "default" },
};

const money = (value) => `${Number(value || 0).toLocaleString("vi-VN")} ₫`;

const renderMethod = (method) => {
    const item = METHOD_LABEL[method];
    return item ? <Tag color={item.color}>{item.text}</Tag> : <Tag>{method || "-"}</Tag>;
};

/**
 * Drawer xem chi tiết một giao dịch thanh toán.
 *
 * Bảng lịch sử chỉ liệt kê từng lần nộp rời rạc, nhìn một dòng không biết khoản
 * thu đó thuộc đợt nào, đã nộp mấy lần, còn nợ bao nhiêu. Drawer gom đủ những
 * thứ đó lại, mở ngay trên danh sách để giữ nguyên bộ lọc đang bật.
 *
 * Tự gọi API khi mở thay vì nhận nguyên dòng của bảng, vì danh sách cố tình
 * không nạp sẵn khoản thu, tài sản, đợt thu và các lần nộp để payload nhẹ.
 *
 * @param {boolean} open
 * @param {number|string|null} paymentId
 * @param {function} onClose
 */
export default function PaymentDetailDrawer({ open, paymentId, onClose }) {
    const navigate = useNavigate();
    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !paymentId) return;

        let ignore = false;

        const fetchDetail = async () => {
            setLoading(true);
            try {
                const response = await paymentAPIWithErrorHandling.getHistoryDetail(paymentId);
                // withErrorHandling đã bóc một lớp, còn lại {success, data, message}.
                const data = response?.data || response;
                if (!ignore) setPayment(data);
            } catch (error) {
                if (!ignore) {
                    setPayment(null);
                    message.error(error.message || "Không thể tải chi tiết giao dịch");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        fetchDetail();

        // Đóng rồi mở giao dịch khác thật nhanh -> bỏ kết quả của lần gọi cũ.
        return () => {
            ignore = true;
        };
    }, [open, paymentId]);

    const handleClose = () => {
        setPayment(null);
        onClose?.();
    };

    const fee = payment?.customer_fee;
    const customer = fee?.customer;
    const collection = fee?.collection;
    const payments = fee?.payments || [];
    const remaining = fee ? Math.max(0, Number(fee.amount_due || 0) - Number(fee.amount_paid || 0)) : 0;
    const feeStatus = fee ? FEE_STATUS_LABEL[fee.status] : null;

    // Nộp tay từ CMS có lúc lưu note là "0" — coi như trống.
    const note = payment?.note && payment.note !== "0" ? payment.note : null;

    return (
        <Drawer
            title="Chi tiết giao dịch"
            width="min(620px, 100vw)"
            open={open}
            onClose={handleClose}
            destroyOnHidden
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={handleClose}>Đóng</Button>
                    {customer?.id && (
                        <Button
                            type="primary"
                            icon={<UserOutlined />}
                            onClick={() => navigate(`/customers/information/${customer.id}`)}
                        >
                            Xem khách hàng
                        </Button>
                    )}
                </Space>
            }
        >
            <Spin spinning={loading}>
                {!payment && !loading ? (
                    <Empty description="Không tìm thấy giao dịch" />
                ) : (
                    <>
                        <div style={{ textAlign: "center", padding: "4px 0 16px" }}>
                            <div
                                style={{
                                    fontSize: 28,
                                    fontWeight: 600,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                                className="text-success-text"
                            >
                                {money(payment?.amount)}
                            </div>
                            <Text type="secondary" className="text-xs">
                                {METHOD_LABEL[payment?.method]?.text || payment?.method || "-"}
                                {payment?.created_at &&
                                    ` · ${dayjs(payment.created_at).format("HH:mm DD/MM/YYYY")}`}
                            </Text>
                        </div>

                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Mã giao dịch">
                                {payment?.transaction_id ? (
                                    <Text copyable={{ text: payment.transaction_id }} code>
                                        {payment.transaction_id}
                                    </Text>
                                ) : (
                                    "-"
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ngày giao dịch">
                                {payment?.payment_date
                                    ? dayjs(payment.payment_date).format("DD/MM/YYYY")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Hình thức">
                                {renderMethod(payment?.method)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Người ghi nhận">
                                {payment?.pay_user?.username || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ghi chú">{note || "-"}</Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" plain style={{ marginTop: 20 }}>
                            Khách hàng
                        </Divider>
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Họ tên">
                                <Text strong>{customer?.name || fee?.customer_name || "-"}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Điện thoại">
                                {customer?.phone || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Địa chỉ thu">
                                {fee?.asset?.name || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        {fee && (
                            <>
                                <Divider orientation="left" plain style={{ marginTop: 20 }}>
                                    Khoản thu
                                </Divider>
                                <Descriptions column={1} bordered size="small">
                                    <Descriptions.Item label="Mã phiếu thu">
                                        {fee.code || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Đợt thu">
                                        {collection?.parent?.name || collection?.name || "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Khu phố">
                                        {collection?.organization?.name ? (
                                            <Tag color="geekblue">{collection.organization.name}</Tag>
                                        ) : (
                                            "-"
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Hạn nộp">
                                        {fee.due_date ? dayjs(fee.due_date).format("DD/MM/YYYY") : "-"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Trạng thái">
                                        {feeStatus ? (
                                            <Tag color={feeStatus.color}>{feeStatus.text}</Tag>
                                        ) : (
                                            "-"
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Phải thu">
                                        {money(fee.amount_due)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Đã thu">
                                        <Text className="text-success-text">
                                            {money(fee.amount_paid)}
                                        </Text>
                                    </Descriptions.Item>
                                    {remaining > 0 && (
                                        <Descriptions.Item label="Còn lại">
                                            <Text strong className="text-danger-text">
                                                {money(remaining)}
                                            </Text>
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                            </>
                        )}

                        {/* Một lần nộp thì trùng với khối trên, không cần liệt kê lại. */}
                        {payments.length > 1 && (
                            <>
                                <Divider orientation="left" plain style={{ marginTop: 20 }}>
                                    {`Các lần nộp (${payments.length})`}
                                </Divider>
                                <Descriptions column={1} bordered size="small">
                                    {payments.map((item) => {
                                        const isCurrent = item.id === payment?.id;
                                        return (
                                            <Descriptions.Item
                                                key={item.id}
                                                label={
                                                    <span style={{ fontWeight: isCurrent ? 600 : 400 }}>
                                                        {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
                                                        {isCurrent && " · lần này"}
                                                    </span>
                                                }
                                            >
                                                <Space>
                                                    {renderMethod(item.method)}
                                                    <Text strong={isCurrent}>{money(item.amount)}</Text>
                                                </Space>
                                            </Descriptions.Item>
                                        );
                                    })}
                                </Descriptions>
                            </>
                        )}
                    </>
                )}
            </Spin>
        </Drawer>
    );
}
